/**
 * input/grab.ts — Proximity-based grab manager (M2.5 skeletal, extended in M4)
 *
 * Interaction rule:
 *   Hands empty → thumbsticks move the player.
 *   Hand holding yoke/throttle → that hand's inputs pilot the ship;
 *     player locomotion from that controller is disabled while held.
 *
 * Grab: trigger OR grip within 15 cm of a handle.
 * Highlight: emissive pulse when in range.
 * Hold-to-keep: releasing the button releases the handle.
 */

import * as THREE from 'three';

export interface Grabbable {
  mesh: THREE.Object3D;
  id: string;
  grabPoint: THREE.Vector3; // local-space attachment point on the grabbable
  onGrab?: (hand: 'left' | 'right') => void;
  onRelease?: (hand: 'left' | 'right') => void;
  highlightMat?: THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
}

const GRAB_RADIUS = 0.15; // 15 cm

// Per-hand state
interface HandState {
  grabbed: Grabbable | null;
  controller: THREE.Group;
  grip: THREE.Group;
}

const _leftHand: HandState = { grabbed: null, controller: null as any, grip: null as any };
const _rightHand: HandState = { grabbed: null, controller: null as any, grip: null as any };

let _grabbables: Grabbable[] = [];
let _highlighted: Grabbable | null = null;
let _highlightPulse = 0;

// Temp objects
const _tmpVec = new THREE.Vector3();

export function initGrab(
  leftCtrl: THREE.Group,
  leftGrip: THREE.Group,
  rightCtrl: THREE.Group,
  rightGrip: THREE.Group,
): void {
  _leftHand.controller = leftCtrl;
  _leftHand.grip = leftGrip;
  _rightHand.controller = rightCtrl;
  _rightHand.grip = rightGrip;
}

export function registerGrabbable(g: Grabbable): void {
  _grabbables.push(g);
}

/** Check if a specific hand is currently holding something */
export function isHandOccupied(hand: 'left' | 'right'): boolean {
  return hand === 'left' ? _leftHand.grabbed !== null : _rightHand.grabbed !== null;
}

/** Get the grabbable held by a hand (null if empty) */
export function getGrabbed(hand: 'left' | 'right'): Grabbable | null {
  return hand === 'left' ? _leftHand.grabbed : _rightHand.grabbed;
}

/**
 * Update grab state each frame.
 * Returns { leftOccupied, rightOccupied } for locomotion gating.
 */
export function updateGrab(input: {
  leftTrigger: number;
  rightTrigger: number;
  leftGrip: number;
  rightGrip: number;
}): { leftOccupied: boolean; rightOccupied: boolean } {
  // Update highlight pulse
  _highlightPulse += 0.05;

  // Process each hand
  _processHand('left', input.leftTrigger > 0.5 || input.leftGrip > 0.5, _leftHand);
  _processHand('right', input.rightTrigger > 0.5 || input.rightGrip > 0.5, _rightHand);

  // Highlight nearest grabbable for each empty hand
  _updateHighlights();

  return {
    leftOccupied: _leftHand.grabbed !== null,
    rightOccupied: _rightHand.grabbed !== null,
  };
}

function _processHand(hand: 'left' | 'right', pressed: boolean, state: HandState): void {
  const ctrl = state.controller;
  if (!ctrl) return;

  ctrl.getWorldPosition(_tmpVec);

  if (state.grabbed) {
    // Holding — check release
    if (!pressed) {
      // Release
      if (state.grabbed.onRelease) state.grabbed.onRelease(hand);
      state.grabbed = null;
      _pulseHaptics(hand, 0.2, 20);
    } else {
      // Still holding — keep attached
      _attachGrabbedToController(state);
    }
  } else {
    // Empty hand — check for grab
    if (pressed) {
      const nearest = _findNearest(_tmpVec);
      if (nearest) {
        state.grabbed = nearest;
        if (nearest.onGrab) nearest.onGrab(hand);
        _pulseHaptics(hand, 0.3, 30);
        _attachGrabbedToController(state);
      }
    }
  }
}

function _findNearest(handPos: THREE.Vector3): Grabbable | null {
  let best: Grabbable | null = null;
  let bestDist = GRAB_RADIUS;

  for (const g of _grabbables) {
    // Skip if already grabbed by other hand
    if (_leftHand.grabbed === g || _rightHand.grabbed === g) continue;

    g.mesh.getWorldPosition(_tmpVec);
    const dist = handPos.distanceTo(_tmpVec);
    if (dist < bestDist) {
      bestDist = dist;
      best = g;
    }
  }
  return best;
}

function _attachGrabbedToController(state: HandState): void {
  if (!state.grabbed || !state.controller) return;
  // M2.5 placeholder: yoke/throttle stay world-anchored.
  // M4 will implement real kinematics (yoke rotates, throttle slides).
  // For now, the grab state gates locomotion.
}

function _updateHighlights(): void {
  // Reset all highlights
  if (_highlighted) {
    _setHighlight(_highlighted, false);
    _highlighted = null;
  }

  // Find nearest for each empty hand
  for (const hand of [_leftHand, _rightHand]) {
    if (hand.grabbed || !hand.controller) continue;
    hand.controller.getWorldPosition(_tmpVec);
    const nearest = _findNearest(_tmpVec);
    if (nearest) {
      _highlighted = nearest;
      _setHighlight(nearest, true);
      break;
    }
  }
}

function _setHighlight(g: Grabbable, on: boolean): void {
  const mats = g.highlightMat;
  if (!mats) return;

  const matsArr = Array.isArray(mats) ? mats : [mats];
  for (const mat of matsArr) {
    if (on) {
      mat.emissive = new THREE.Color(0x4488ff);
      mat.emissiveIntensity = 0.3 + 0.2 * Math.sin(_highlightPulse * 3);
    } else {
      mat.emissive = new THREE.Color(0x000000);
      mat.emissiveIntensity = 0;
    }
  }
}

function _pulseHaptics(hand: 'left' | 'right', intensity: number, ms: number): void {
  // Stub — haptics wired in M4 when we have session reference
  // M2.5 just tracks grab state for locomotion gating
  void hand; void intensity; void ms;
}
